import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import {
  Coins, RefreshCw, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  Search, Filter, BarChart3, Users, TrendingUp, Scale,
} from 'lucide-react';

const TYPE_LABELS = {
  reward:         'Reward',
  purchase:       'Purchase',
  transfer:       'Transfer',
  donation:       'Donation',
  asset_purchase: 'Asset Purchase',
  credit:         'Credit',
  debit:          'Debit',
  refund:         'Refund',
};

const TYPE_COLORS = {
  reward:         'text-emerald-500 bg-emerald-500/10 border-emerald-500/25',
  purchase:       'text-[hsl(var(--mg-gold))] bg-[hsl(var(--mg-gold)/0.1)] border-[hsl(var(--mg-gold-deep)/0.3)]',
  transfer:       'text-sky-400 bg-sky-400/10 border-sky-400/25',
  donation:       'text-[hsl(var(--mg-purple))] bg-[hsl(var(--mg-purple)/0.1)] border-[hsl(var(--mg-purple)/0.3)]',
  asset_purchase: 'text-orange-400 bg-orange-400/10 border-orange-400/25',
  refund:         'text-rose-400 bg-rose-400/10 border-rose-400/25',
};

function TypeChip({ type }) {
  const cls = TYPE_COLORS[type] || 'text-muted-foreground bg-muted border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, gold }) {
  return (
    <div className={`mg-card-elevated p-4 flex flex-col gap-2 ${gold ? 'border-[hsl(var(--mg-gold-deep)/0.3)]' : ''}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
        <Icon className={`w-4 h-4 ${gold ? 'text-[hsl(var(--mg-gold))]' : ''}`} />
        {label}
      </div>
      <div className={`text-2xl font-black tabular-nums ${gold ? 'mg-gold-text' : 'text-foreground'}`}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function ReconciliationBanner({ recon }) {
  if (!recon) return null;
  const clean = recon.status === 'clean';
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${
      clean
        ? 'border-emerald-500/25 bg-emerald-500/5 text-emerald-500'
        : 'border-amber-500/25 bg-amber-500/5 text-amber-400'
    }`}>
      {clean
        ? <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
        : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
      <div>
        <span className="font-semibold">{clean ? 'Ledger balanced' : 'Historical gap detected'}</span>
        <span className="ml-2 opacity-80">{recon.note}</span>
      </div>
    </div>
  );
}

export default function TreasuryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [txData, setTxData] = useState(null);
  const [recon, setRecon] = useState(null);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingTx, setLoadingTx] = useState(true);
  const [loadingRecon, setLoadingRecon] = useState(false);
  const [showRecon, setShowRecon] = useState(false);

  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Auth guard: admin only
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/home');
    }
  }, [user, navigate]);

  const fetchOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      const r = await api.get('/admin/treasury/overview');
      setOverview(r.data);
    } catch {
      setOverview(null);
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchTx = useCallback(async () => {
    setLoadingTx(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (typeFilter) params.set('type_filter', typeFilter);
      if (search) params.set('search', search);
      const r = await api.get(`/admin/treasury/transactions?${params}`);
      setTxData(r.data);
    } catch {
      setTxData(null);
    } finally {
      setLoadingTx(false);
    }
  }, [page, typeFilter, search]);

  const fetchRecon = useCallback(async () => {
    setLoadingRecon(true);
    try {
      const r = await api.get('/admin/treasury/reconciliation');
      setRecon(r.data);
    } catch {
      setRecon(null);
    } finally {
      setLoadingRecon(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchTx(); }, [fetchTx]);
  useEffect(() => {
    if (showRecon && !recon) fetchRecon();
  }, [showRecon, recon, fetchRecon]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleTypeFilter = (t) => {
    setTypeFilter(t === typeFilter ? '' : t);
    setPage(1);
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="mg-page max-w-5xl py-6 space-y-6" data-testid="treasury-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--mg-gold)/0.12)] border border-[hsl(var(--mg-gold-deep)/0.3)] flex items-center justify-center">
            <Coins className="w-5 h-5 text-[hsl(var(--mg-gold))] mg-gold-glow" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl">The Runegold Treasury</h1>
            <p className="text-sm text-muted-foreground">Read-only economy overview — Stage 7A</p>
          </div>
        </div>
        <button
          onClick={() => { fetchOverview(); fetchTx(); if (showRecon) fetchRecon(); }}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Overview Stats */}
      {loadingOverview ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : overview ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={Coins}
              label="In Circulation"
              value={overview.circulation.total_runegold.toLocaleString()}
              sub="Total Runegold held by users"
              gold
            />
            <StatCard
              icon={Users}
              label="Wallets"
              value={overview.circulation.wallet_holders}
              sub={`of ${overview.circulation.total_accounts} accounts`}
            />
            <StatCard
              icon={TrendingUp}
              label="Ledger-Tracked"
              value={overview.ledger_issuance.total_tracked.toLocaleString()}
              sub={`${overview.ledger_issuance.operation_count} operations`}
            />
            <StatCard
              icon={BarChart3}
              label="Pre-Service"
              value={overview.ledger_issuance.untracked_pre_service.toLocaleString()}
              sub="Seeded before ledger"
            />
          </div>

          {/* Issuance breakdown */}
          {Object.keys(overview.ledger_issuance.by_type).length > 0 && (
            <div className="mg-card-elevated p-4">
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Issuance by Type</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.ledger_issuance.by_type).map(([type, data]) => (
                  <div key={type} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-sm">
                    <TypeChip type={type} />
                    <span className="font-semibold tabular-nums">{data.total.toLocaleString()}</span>
                    <span className="text-muted-foreground text-xs">({data.count}×)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ReconciliationBanner recon={overview.reconciliation} />
        </>
      ) : (
        <div className="mg-card-elevated p-6 text-center text-muted-foreground">
          Failed to load overview
        </div>
      )}

      {/* Reconciliation Detail (lazy) */}
      <div>
        <button
          onClick={() => setShowRecon(v => !v)}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <Scale className="w-4 h-4" />
          {showRecon ? 'Hide' : 'Show'} reconciliation detail
        </button>

        {showRecon && (
          <div className="mt-3 mg-card-elevated p-4 space-y-4">
            {loadingRecon ? (
              <div className="h-20 animate-pulse bg-muted rounded-lg" />
            ) : recon ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Authoritative total</p>
                    <p className="font-bold tabular-nums">{recon.summary.total_authoritative.toLocaleString()}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Ledger net</p>
                    <p className="font-bold tabular-nums">{recon.summary.total_ledger_net.toLocaleString()}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Gap</p>
                    <p className={`font-bold tabular-nums ${recon.summary.gap > 0 ? 'text-amber-400' : 'text-emerald-500'}`}>
                      {recon.summary.gap.toLocaleString()}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Tracked users</p>
                    <p className="font-bold tabular-nums">{recon.summary.tracked_users} / {recon.summary.tracked_users + recon.summary.untracked_users}</p>
                  </div>
                </div>

                {recon.untracked_users.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Users without ledger entries ({recon.untracked_users.length})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground border-b border-border">
                            <th className="text-left py-1.5 pr-4">Username</th>
                            <th className="text-right py-1.5 pr-4">Balance</th>
                            <th className="text-left py-1.5">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recon.untracked_users.map(u => (
                            <tr key={u.user_id} className="border-b border-border/50">
                              <td className="py-1.5 pr-4 font-medium">@{u.username}</td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">{u.balance}</td>
                              <td className="py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${u.is_npc ? 'bg-[hsl(var(--mg-purple)/0.15)] text-[hsl(var(--mg-purple))]' : 'bg-muted text-muted-foreground'}`}>
                                  {u.is_npc ? 'NPC' : 'User'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {recon.discrepancies.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">
                      Balance discrepancies ({recon.discrepancies.length})
                    </h4>
                    {recon.discrepancies.map(d => (
                      <div key={d.user_id} className="flex items-center justify-between py-1.5 text-xs border-b border-border/50">
                        <span>@{d.username}</span>
                        <span>balance={d.balance} ledger={d.ledger_net} gap=<span className="text-amber-400">{d.gap}</span></span>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-muted-foreground italic">{recon.note}</p>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Failed to load reconciliation data.</p>
            )}
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading font-bold text-base">Transaction Ledger</h2>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search operations..."
                className="pl-8 pr-3 py-1.5 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--mg-purple)/0.5)] w-44"
              />
            </div>
            <button type="submit" className="text-xs px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors">
              Search
            </button>
            {search && (
              <button type="button" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
                className="text-xs px-2 py-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors">
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Type filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          {Object.keys(TYPE_LABELS).map(t => (
            <button
              key={t}
              onClick={() => handleTypeFilter(t)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                typeFilter === t
                  ? 'bg-[hsl(var(--mg-purple))] border-[hsl(var(--mg-purple))] text-white'
                  : 'border-border text-muted-foreground hover:border-[hsl(var(--mg-steel)/0.4)] hover:text-foreground'
              }`}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
          {typeFilter && (
            <button onClick={() => handleTypeFilter('')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Clear filter
            </button>
          )}
        </div>

        {/* Table */}
        {loadingTx ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : txData && txData.transactions.length > 0 ? (
          <div className="mg-card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Time</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">From</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">To</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Description</th>
                    <th className="text-left px-4 py-3 hidden xl:table-cell">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {txData.transactions.map(tx => (
                    <tr key={tx.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        <br />
                        <span className="text-[10px]">{new Date(tx.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <TypeChip type={tx.type} />
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                        <span className="text-[hsl(var(--mg-gold))]">+{tx.amount}</span>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-xs">
                        {tx.initiator
                          ? <span title={tx.initiator_user_id}>@{tx.initiator.username}</span>
                          : <span className="text-muted-foreground">system</span>}
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-xs">
                        {tx.target
                          ? <span title={tx.target_user_id}>@{tx.target.username}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground max-w-[200px] truncate">
                        {tx.description}
                      </td>
                      <td className="px-4 py-2.5 hidden xl:table-cell text-[10px] text-muted-foreground font-mono">
                        {tx.id?.slice(0, 8)}…
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {txData.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
                <span className="text-muted-foreground text-xs">
                  Page {txData.page} of {txData.pages} · {txData.total} total
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(txData.pages, p + 1))}
                    disabled={page >= txData.pages}
                    className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mg-card-elevated p-8 text-center text-muted-foreground text-sm">
            {search || typeFilter ? 'No transactions match the current filter.' : 'No transactions recorded yet.'}
          </div>
        )}
      </div>
    </div>
  );
}
